import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import LegalDisclaimer from "@/components/LegalDisclaimer";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <div className="p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-red-600" data-testid="back-to-home">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img src="/imcac_logo.png" alt="IMCAC" className="h-14 w-14 object-contain mb-4" />
            <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-2">IMCAC · Log in</div>
            <h1 className="font-display text-4xl font-bold tracking-tight">Welcome back.</h1>
            <p className="text-zinc-600 mt-2 text-sm">Continue your IMCAC Bloodborne Pathogens certification.</p>
          </div>

          <div className="bg-white border-2 border-zinc-900 rounded-lg p-8">
            <Button
              type="button"
              onClick={googleLogin}
              variant="outline"
              className="w-full h-11 border-zinc-900 hover:bg-zinc-50 mb-4"
              data-testid="google-login-btn"
            >
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-zinc-200" />
              <span className="text-xs font-mono uppercase text-zinc-500">or</span>
              <div className="flex-1 h-px bg-zinc-200" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider font-mono">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-wider font-mono">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="mt-1.5 h-11"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-red-600 hover:bg-red-700 text-white"
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in…" : "Log in"}
              </Button>
            </form>
          </div>

          <p className="text-sm text-zinc-600 mt-6 text-center">
            Don't have an account?{" "}
            <Link to="/register" className="text-red-600 font-medium hover:underline" data-testid="link-to-register">
              Create one
            </Link>
          </p>
        </div>
      </div>

      <footer className="mt-16 bg-zinc-900 py-10">
        <div className="max-w-4xl mx-auto px-6">
          <LegalDisclaimer variant="footer" />
        </div>
      </footer>
    </div>
  );
}
